/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import ArrowIcon from '@mui/icons-material/TrendingFlat'
import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'
import HEHMTermsAndConditionsModal from './HEHMTermsAndConditionsModal'
import Spinner from 'components/Spinner'

import { useFeaturesContext } from 'hooks/FeaturesContext'
import useHehmTermsApi from 'hooks/useHehmTermsApi'
import usePaywallPlans from 'hooks/usePaywallPlans'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'
import Brand from 'helpers/brand'

const UNLOCK_TITLE = 'Unlock Greater Connectivity'
const PREMIUM_EXPLANATION = 'By activating HVAC Performance Monitoring Kit, Dealers create enduring relationships with customers through innovative connectivity.'
const WHY_TITLE = 'Dealer Benefits'
const WHY_REGISTER = [
  'Real-time monitoring of system performance for proactive maintenance',
  'Extended equipment health and lifespan',
  'Deepened customer trust',
  'Reduced service visits'
]
const PRICING_TITLE = 'Pricing'
const PRICING = [
  <span key={1}>
    You will be charged $6 plus applicable taxes per installed kit.
    <br />
    <span style={{ fontStyle: 'italic' }}>flexible, cancel anytime</span>
  </span>
]

const HEHMOptions = () => {
  const { dealerId } = useParams()
  const navigate = useNavigate()
  const { enabledFeatures } = useFeaturesContext()

  const planType = 'hehm_monthly'
  const { hehmTermsUpToDate, isHehmTermsLoaded } = useHehmTermsApi()
  const { plans, monthlyHEHMPlanNo, arePaywallPlansLoaded } = usePaywallPlans(dealerId)
  const [currentPlan, setCurrentPlan] = useState(monthlyHEHMPlanNo)
  const [displayTermsModal, setDisplayTermsModal] = useState(false)

  useEffect(() => {
    if (arePaywallPlansLoaded) {
      setCurrentPlan(monthlyHEHMPlanNo)
    }
  }, [arePaywallPlansLoaded, plans, monthlyHEHMPlanNo])

  if (!arePaywallPlansLoaded || !isHehmTermsLoaded) {
    return (
      <S.EmptyPage>
        <Spinner variant="page" />
      </S.EmptyPage>
    )
  }

  const onCloseModalCallback = () => {
    setDisplayTermsModal(false)
  }

  const checkIfAcceptedTerms = () => {
    if (hehmTermsUpToDate || enabledFeatures?.includes('beta_tcs')) {
      navigateToBilling()
    } else {
      setDisplayTermsModal(true)
    }
  }

  const navigateToBilling = () => {
    navigate(`/ehmkt/account/${dealerId}/${currentPlan}/${planType}`)
  }

  return (
    <>
      <S.Top>
        <Button decoration="link" onPress={() => navigate(-1)} aria-label="back-button">
          <S.ArrowIcon fontSize="large" />
        </Button>
        <S.ProductLogo src={Brand.hehmProductLogoPath()} alt="HEHM system logo" />
      </S.Top>
      <S.Background>
        <S.Content>
          <S.LeftSide>
            <S.FormSection>
              <S.SectionHeader>{UNLOCK_TITLE}</S.SectionHeader>
              <S.Explanation>{PREMIUM_EXPLANATION}</S.Explanation>
            </S.FormSection>
            <S.FormSection>
              <S.SectionHeader>{WHY_TITLE}</S.SectionHeader>
              {WHY_REGISTER.map((reason) => (
                <S.Explanation key={reason}>
                  <S.Image src={'/img/checkmark.png'} width={40} />
                  <span>
                    {reason.split(' -')[0]}
                  </span>
                </S.Explanation>
              ))}
            </S.FormSection>
            <S.FormSection>
              <S.SectionHeader>{PRICING_TITLE}</S.SectionHeader>
              {PRICING.map((price, index) => (
                <S.Explanation key={price.props.children} style={index === 1 ? { marginLeft: 20, marginTop: 0, alignItems: 'center' } : {}}>
                  <S.PriceArrowIcon fontSize={index === 1 ? 'medium' : 'large'} />
                  {price}
                </S.Explanation>
              ))}
            </S.FormSection>
          </S.LeftSide>
        </S.Content>
        <S.ButtonContainer>
          <Button
            decoration="positive"
            text={'Activate Account'}
            onPress={checkIfAcceptedTerms}
            styles={UpgradeButtonStyle}
          />
        </S.ButtonContainer>
        { (hehmTermsUpToDate && !enabledFeatures?.includes('beta_tcs')) &&
          <S.TermsLink onClick={() => setDisplayTermsModal(true)}>Terms & Conditions</S.TermsLink>
        }
      </S.Background>
      {displayTermsModal &&
        <HEHMTermsAndConditionsModal
          closeCallback={onCloseModalCallback}
          navigateToBilling={navigateToBilling}
          hehmTermsUpToDate={hehmTermsUpToDate || false}
        />}
    </>
  )
}

export default HEHMOptions

const UpgradeButtonStyle: ButtonStyles = {
  button: { height: 46, borderRadius: 10, width: 200, backgroundColor: color('upgradeButton') },
  text: { textTransform: 'capitalize', fontWeight: 'bold' }
}

const blueUnderlineGradient = `
  background-image: linear-gradient(179deg, ${color('hehmBorderGradientTopLeft')} 0%, ${color('hehmBorderGradientFiftyPercent')} 50%,transparent 54%, transparent 100%);
  background-size:  160% 12%;
  background-repeat: repeat-x;
  background-position: left 0% bottom 10%;
`

const S = {
  EmptyPage: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    width: 100vw;
  `,
  Background: styled.div`
    min-height: calc(100vh - 92px);
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-image: url('/img/hehm_background.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-attachment: fixed;

    @media ${screenSize.small} {
      margin-bottom: -60px;
      background-size: 125% 100%;
      justify-content: flex-start;
      min-height: calc(100vh - 74px);
    }
  `,
  ArrowIcon: styled(ArrowIcon)`
    transform: rotate(180deg);
    color: ${color('breadCrumbsTextHover')};
  `,
  ButtonContainer: styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 5rem;

    @media ${screenSize.small} {
      margin-top: 2rem;
    }
  `,
  PriceArrowIcon: styled(ArrowIcon)`
    color: ${color('primaryAccent')};
    margin: -5px 8px;
  `,
  Container: styled.div`
    padding: 20px;
  `,
  Logo: styled.div`
    align-self: center;
    font-size: 65px;
    color: ${color('disabledText')};
    margin: 30px;

    @media ${screenSize.small} {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      gap: 20px
    }
  `,
  Content: styled.form`
    display: flex;
    justify-content: center;
    gap: 20px;
    width: 100%;
    max-width: 1200px;
    flex-wrap: wrap;
  `,
  FormSection: styled.div`
    & > div {
      margin: 10px 0 0 0;

      & label {
        font-weight: bold;
      }
    }
  `,
  RedText: styled.span`
    color: ${color('premiumFreeTrialText')};
  `,
  RightSideHeader: styled.h3`
    margin-top: 25px;
    margin-bottom: 40px;
    text-align: center;
    text-transform: none;
    font-size: 18px;
    font-style: italic;

    @media ${screenSize.small} {
      line-height: 1.2;
    }
  `,
  SectionHeader: styled.h1`
    ${blueUnderlineGradient}

    margin-top: 25px;
    padding-bottom: 12px;
    text-transform: unset;
    width: fit-content;
  `,
  LeftSide: styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 550px;
    word-wrap: break-word;

    @media ${screenSize.medium} {
      margin-top: 0;
    }
  `,
  RightSide: styled.div`
    max-width: 720px;

    @media ${screenSize.medium} {
      margin-top: 10px;
    }
  `,
  Explanation: styled.div`
    display: flex;
    margin: 0 auto 20px;
    text-align: left;
  `,
  Image: styled.img`
    align-self: flex-end;
    height: 20px;
    width: 20px;
    margin: 0 10px 10px 0;
    cursor: pointer;
    z-index: 1000;
  `,
  ProductLogo: styled.img`
    margin: 10px auto;
    height: 50px;
    @media ${screenSize.small} {
      height: 35px;
    }
  `,
  BlueLineForTextUnderBoxes: styled.div`
    ${blueUnderlineGradient}
    height: 30px;
    width: 200px;
    margin-left: 30px;
    margin-top: -15px;
  `,
  TextUnderBoxes: styled.div`
    margin: 30px auto;
    width: 270px;
    font-weight: bold;
    text-align: center;
  `,
  Top: styled.div`
    display: flex;
    padding: 20px;
    border-bottom: 1px solid ${color('hehmCardBorder')};
    background-color: ${color('hehmCardBackground')};
  `,
  Note: styled.div`
    display: flex;
    margin-top: 25px;
    text-align: left;
    font-style: italic;
    max-width: 720px;
  `,
  TermsLink: styled.a`
    margin-top: 20px;
    text-decoration: underline;
    cursor: pointer;
  `
}
